import { FC, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import styles from 'styles/components/card.module.scss'
import text from 'styles/utility/text.module.scss'
import { FlexRow } from 'components/Flex'
import { Spacer } from 'components/Utility'
import { LightTag } from 'components/Tag'
import { BlueButton } from 'components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { ScheduleType } from 'types/schedule'
import { openModal, openStockModal } from 'reducks/modal/operations'
import { StockType } from 'types/stock'
import { sumCheckoutItemsAmount, sumStocksAmount } from 'lib/helper'
import { BreadType } from 'types/bread'
import { CheckoutItemType } from 'types/checkoutItem'
import { format, isSameDay } from 'date-fns'
import { State } from 'index'
import { getRole } from 'reducks/auth/selectors'
import { Role } from 'lib/role'
import { getClosedDates } from 'reducks/calendar/selectors'
import { IndividualPreorderScheduleModal } from 'components/Modal'

type Props = {
  bread: BreadType
  checkoutItems: CheckoutItemType[]
  checkedOrderTypes: string[]
  currentTab: 'released' | 'unreleased'
  date: Date
  isAfterLastOrder: boolean
  isBeforePreorderDeadline?: boolean
  isFuture: boolean
  schedule: ScheduleType | undefined
  stocks: StockType[]
  type: 'preorder' | 'daily'
}

const ScheduledItemStockCard: FC<Props> = (props) => {
  const {
    bread,
    checkoutItems,
    checkedOrderTypes,
    currentTab,
    date,
    isAfterLastOrder,
    isBeforePreorderDeadline,
    isFuture,
    schedule,
    stocks,
    type,
  } = props
  const dispatch = useDispatch()

  const closedDates = useSelector((state: State) => getClosedDates(state))
  const role = useSelector((state: State) => getRole(state))
  const roleManager = new Role(role)

  const now = new Date()
  const isClosed = closedDates.some((closedDate) => isSameDay(closedDate, date))
  const isToday = isSameDay(date, now)
  const editPreorderStock = useCallback(() => {
    dispatch(
      openModal(
        <IndividualPreorderScheduleModal bread={bread} checkedOrderTypes={checkedOrderTypes} date={date} />,
        `${format(date, 'M月d日')}の設定を変更`
      )
    )
  }, [dispatch, bread, date, checkedOrderTypes])

  const editDailyStock = useCallback(() => {
    // 販売予定画面で、当日の販売予定商品の編集モーダルを開いた時、”お客様に通知する”ボタンを表示させるために現在時刻を付与
    const selectedDate = new Date(date)
    selectedDate.setHours(now.getHours())
    selectedDate.setMinutes(now.getMinutes())
    selectedDate.setSeconds(now.getSeconds())
    dispatch(openStockModal(bread, schedule ? [schedule] : [], stocks, selectedDate))
  }, [dispatch, stocks, schedule, bread, date])

  const isEditable = useMemo(() => {
    if (!roleManager.hasEnoughRole(9)) return false
    switch (type) {
      // 当日取り置きはラストオーダー前または翌営業日以降であれば編集可能
      case 'daily':
        return (isToday && !isAfterLastOrder) || (isFuture && !isClosed)
      // 事前予約は注文締切前であれば編集可能
      case 'preorder':
        return isBeforePreorderDeadline
      default:
        return false
    }
  }, [roleManager, isAfterLastOrder, isClosed, isFuture, isToday, isBeforePreorderDeadline])

  type Amount = {
    amount: number
    stocksAmount: number
    checkoutItemsAmount: number
  }
  const { amount, stocksAmount, checkoutItemsAmount }: Amount = useMemo((): Amount => {
    const stocksAmount = sumStocksAmount(stocks)
    const checkoutItemsAmount = sumCheckoutItemsAmount(checkoutItems)
    if (type === 'daily' && checkoutItems) {
      if (stocks.length === 0) {
        if (!schedule) {
          return {
            amount: 0,
            stocksAmount,
            checkoutItemsAmount,
          }
        }
        const amount = schedule.default_stocks.reduce((prev, defaultStock) => prev + defaultStock.stock, 0)
        return {
          amount,
          stocksAmount: amount,
          checkoutItemsAmount,
        }
      } else {
        return {
          amount: stocksAmount - checkoutItemsAmount,
          stocksAmount,
          checkoutItemsAmount,
        }
      }
    } else if (type === 'preorder') {
      return {
        amount: stocksAmount - checkoutItemsAmount,
        stocksAmount,
        checkoutItemsAmount,
      }
    } else {
      return {
        amount: 0,
        stocksAmount,
        checkoutItemsAmount,
      }
    }
  }, [type, checkoutItems, stocks, schedule, isFuture, isEditable])

  return (
    <div className={styles.grey}>
      <FlexRow justifyContent={'between'}>
        <div>
          <FlexRow justifyContent={'start'}>
            <span className={classNames(text.font_small, text.color_grey_01)}>販売予定 ： </span>
            {type === 'daily' && <LightTag color={'blue'} label={'当日'} />}
            {type === 'preorder' && <LightTag color={'green'} label={'事前予約'} />}
          </FlexRow>
          {currentTab === 'released' && (
            <>
              <Spacer size={8} />
              <FlexRow justifyContent={'start'}>
                <span className={classNames(text.font_small, text.color_grey_01, text.font_bold)}>在庫数 ： </span>
                <span className={classNames(text.font_small, text.font_bold)}>{amount}個</span>
              </FlexRow>
              <Spacer size={8} />
              <FlexRow justifyContent={'start'}>
                <span className={classNames(text.font_small, text.color_grey_01)}>設定数 ： </span>
                <span className={text.font_small}>{stocksAmount}個</span>
              </FlexRow>
              <Spacer size={8} />
              <FlexRow justifyContent={'start'}>
                <span className={classNames(text.font_small, text.color_grey_01)}>注文数 ： </span>
                <span className={text.font_small}>{checkoutItemsAmount}個</span>
              </FlexRow>
            </>
          )}
        </div>
        {isEditable && (
          <BlueButton
            label={type === 'daily' ? '編集' : '個別設定'}
            onClick={type === 'daily' ? editDailyStock : editPreorderStock}
            size={'xxsmall'}
            type={'outlined'}
          />
        )}
      </FlexRow>
    </div>
  )
}

export default ScheduledItemStockCard
